import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings, 
  Copy, 
  Check,
  AlertCircle,
  CheckCircle2,
  Terminal,
  Server,
  Shield,
  ExternalLink
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const SetupPage = () => {
  const { language } = useLanguage();
  const [copiedIndex, setCopiedIndex] = useState(null);

  const translations = {
    tr: {
      title: 'Kurulum Kılavuzu',
      description: 'Cihazınızı Threat.live ile entegre edin ve zararlı IP\'leri otomatik engelleyin',
      selectDevice: 'Cihaz Seçin',
      step: 'Adım',
      copy: 'Kopyala',
      copied: 'Kopyalandı!',
      important: 'Önemli Bilgi',
      setupComplete: 'Kurulum Tamamlandı!',
      // FortiGate
      fortigateTitle: 'FortiGate Threat.live Kurulumu',
      fortigateDesc: 'FortiGate cihazınızı Threat.live ile entegre edin',
      fortigateNote: 'Bu kurulum FortiGate 6.0 ve üzeri sürümler için geçerlidir. Eski sürümlerde bazı komutlar farklılık gösterebilir.',
      fortigateStep1Title: 'External Connector Oluştur',
      fortigateStep1Desc: 'FortiGate\'de Threat.live için external connector oluşturun:',
      fortigateStep2Title: 'Firewall Policy Oluştur',
      fortigateStep2Desc: 'Zararlı IP\'leri engellemek için firewall policy oluşturun:',
      fortigateNatNote: 'İç ağınız için NAT kullanıyorsanız, policy\'deki kaynak interface (srcintf) kısmı WAN interface\'iniz olmalı, hedef adres (dstaddr) ve hedef interface (dstintf) kısımlarına Virtual IP veya ilgili IP adreslerini eklemeniz gerekmektedir. Policy\'nin aktif uygulanabilmesi için ilgili Virtual IP tanımlarının en üstünde olmalıdır.',
      fortigateComplete: 'FortiGate cihazınız artık Threat.live listesinden gelen zararlı IP\'leri otomatik olarak senkronize bir şekilde engelleyecektir.',
      // Juniper
      juniperTitle: 'Juniper Threat.live Kurulumu',
      juniperDesc: 'BGP oturumu ile Threat.live kullanabilirsiniz.',
      juniperNote: 'Juniper cihazınızda Threat.live listesini kullanmak için BGP kurulumu yapabilirsiniz.',
      juniperInfo1: 'BGP bilgilerini Threat.live panelinden öğrenebilirsiniz.',
      juniperInfo2: 'IP adresi sayısı nedeniyle MX serisi router gibi cihazlara kurulması önerilir.',
      // MikroTik
      mikrotikTitle: 'MikroTik Threat.live Kurulumu',
      mikrotikDesc: 'BGP oturumu ile Threat.live kullanabilirsiniz.',
      mikrotikNote: 'MikroTik cihazınızda Threat.live listesini kullanmak için BGP kurulumu yapabilirsiniz.',
      mikrotikInfo1: 'BGP bilgilerini Threat.live panelinden öğrenebilirsiniz.',
      mikrotikInfo2: 'BGP haricinde IP Listesi sayfasından RSC dosyasını indirip import edebilirsiniz.',
      ipListLink: 'IP Listesi Sayfası',
      // Linux
      linuxTitle: 'Linux Kurulumu',
      linuxDesc: 'Router veya Firewall erişimi olmayan ya da sadece sunucu bazında kullanmak isteyen kullanıcılar için Ubuntu, AlmaLinux, Debian vb. uyumlu otomatik kurulum scripti.',
      linuxNote: 'Bu script root yetkisi gerektirir ve iptables/ipset kullanır.',
      linuxStep1Title: 'Kurulum Scripti',
      linuxStep1Desc: 'Aşağıdaki komutu root olarak çalıştırın:',
      linuxFeatures: 'Script şunları yapar:',
      linuxFeature1: 'ipset kurar (yoksa)',
      linuxFeature2: 'Firewall kuralı ekler',
      linuxFeature3: 'Zararlı IP listesini yükler',
      linuxFeature4: 'Her 5 dakikada otomatik günceller',
      linuxUninstall: 'Kaldırmak için:',
      linuxComplete: 'Linux sunucunuz artık Threat.live listesinden gelen zararlı IP\'leri otomatik olarak engelleyecektir.',
      // Linux Commands
      usefulCommands: 'Kullanışlı Komutlar',
      usefulCommandsDesc: 'Kurulum sonrası kullanılabilecek komutlar',
      cmdStatus: 'Durum Kontrolü',
      cmdStatusDesc: 'Sistem durumunu gösterir',
      cmdUpdate: 'Manuel Güncelleme',
      cmdUpdateDesc: 'Timer beklemeden listeyi hemen günceller',
      cmdLogs: 'Logları Görüntüle',
      cmdLogsDesc: 'Sistem loglarını gösterir',
      exampleOutput: 'Örnek Çıktı:',
      // Windows
      windowsTitle: 'Windows Kurulumu',
      windowsDesc: 'Windows Server ve masaüstü sistemler için otomatik kurulum scripti. PowerShell ile Windows Firewall üzerinden zararlı IP\'leri engeller.',
      windowsNote: 'Bu script Administrator yetkisi gerektirir ve Windows Firewall kullanır.',
      windowsStep1Title: 'Kurulum Scripti',
      windowsStep1Desc: 'PowerShell\'i Administrator olarak açın ve aşağıdaki komutu çalıştırın:',
      windowsFeatures: 'Script şunları yapar:',
      windowsFeature1: 'Windows Firewall kuralı ekler',
      windowsFeature2: 'Zararlı IP listesini yükler',
      windowsFeature3: 'Her 10 dakikada otomatik günceller',
      windowsFeature4: 'Scheduled Task ile başlangıçta çalışır',
      windowsUninstall: 'Kaldırmak için:',
      windowsComplete: 'Windows sisteminiz artık Threat.live listesinden gelen zararlı IP\'leri otomatik olarak engelleyecektir.',
      cmdUninstall: 'Kaldır',
      cmdUninstallDesc: 'Threat.live korumasını tamamen kaldırır',
    },
    en: {
      title: 'Setup Guide',
      description: 'Integrate your device with Threat.live and automatically block malicious IPs',
      selectDevice: 'Select Device',
      step: 'Step',
      copy: 'Copy',
      copied: 'Copied!',
      important: 'Important Information',
      setupComplete: 'Setup Completed!',
      // FortiGate
      fortigateTitle: 'FortiGate Threat.live Setup',
      fortigateDesc: 'Integrate your FortiGate device with Threat.live',
      fortigateNote: 'This setup is valid for FortiGate 6.0 and above versions. Some commands may differ in older versions.',
      fortigateStep1Title: 'Create External Connector',
      fortigateStep1Desc: 'Create external connector for Threat.live in FortiGate:',
      fortigateStep2Title: 'Create Firewall Policy',
      fortigateStep2Desc: 'Create firewall policy to block malicious IPs:',
      fortigateNatNote: 'If you are using NAT for your internal network, the source interface (srcintf) in the policy should be your WAN interface, and you need to add Virtual IP or relevant IP addresses to the destination address (dstaddr) and destination interface (dstintf) sections. For the policy to be actively applied, it must be placed above the relevant Virtual IP definitions.',
      fortigateComplete: 'Your FortiGate device will now automatically block malicious IPs coming from the Threat.live list in sync.',
      // Juniper
      juniperTitle: 'Juniper Threat.live Setup',
      juniperDesc: 'You can use Threat.live with BGP session.',
      juniperNote: 'You can set up BGP to use the Threat.live list on your Juniper device.',
      juniperInfo1: 'You can learn BGP information from the Threat.live panel.',
      juniperInfo2: 'Due to the number of IP addresses, it is recommended to install it on devices such as MX series routers.',
      // MikroTik
      mikrotikTitle: 'MikroTik Threat.live Setup',
      mikrotikDesc: 'You can use Threat.live with BGP session.',
      mikrotikNote: 'You can set up BGP to use the Threat.live list on your MikroTik device.',
      mikrotikInfo1: 'You can learn BGP information from the Threat.live panel.',
      mikrotikInfo2: 'Apart from BGP, you can download the RSC file from the IP List page and import it.',
      ipListLink: 'IP List Page',
      // Linux
      linuxTitle: 'Linux Setup',
      linuxDesc: 'Automatic installation script for Ubuntu, AlmaLinux, Debian and similar systems.',
      linuxNote: 'This script requires root privileges and uses iptables/ipset.',
      linuxStep1Title: 'Installation Script',
      linuxStep1Desc: 'Run the following command as root:',
      linuxFeatures: 'The script will:',
      linuxFeature1: 'Install ipset (if not present)',
      linuxFeature2: 'Add firewall rule',
      linuxFeature3: 'Load malicious IP list',
      linuxFeature4: 'Auto-update every 5 minutes',
      linuxUninstall: 'To uninstall:',
      linuxComplete: 'Your Linux server will now automatically block malicious IPs from the Threat.live list.',
      // Linux Commands
      usefulCommands: 'Useful Commands',
      usefulCommandsDesc: 'Commands available after installation',
      cmdStatus: 'Status Check',
      cmdStatusDesc: 'Shows system status',
      cmdUpdate: 'Manual Update',
      cmdUpdateDesc: 'Updates the list immediately without waiting for the timer',
      cmdLogs: 'View Logs',
      cmdLogsDesc: 'Shows system logs',
      exampleOutput: 'Example Output:',
      // Windows
      windowsTitle: 'Windows Setup',
      windowsDesc: 'Automatic installation script for Windows Server and desktop systems. Blocks malicious IPs via Windows Firewall using PowerShell.',
      windowsNote: 'This script requires Administrator privileges and uses Windows Firewall.',
      windowsStep1Title: 'Installation Script',
      windowsStep1Desc: 'Open PowerShell as Administrator and run the following command:',
      windowsFeatures: 'The script will:',
      windowsFeature1: 'Add Windows Firewall rule',
      windowsFeature2: 'Load malicious IP list',
      windowsFeature3: 'Auto-update every 10 minutes',
      windowsFeature4: 'Run at startup via Scheduled Task',
      windowsUninstall: 'To uninstall:',
      windowsComplete: 'Your Windows system will now automatically block malicious IPs from the Threat.live list.',
      cmdUninstall: 'Uninstall',
      cmdUninstallDesc: 'Completely removes Threat.live protection',
    }
  };

  const txt = translations[language] || translations.en;

  const fortigateCode1 = `config system external-resource
    edit "Threat-Live"
        set type address
        set comments "Threat.live"
        set resource "https://list.threat.live/"
    next
end`;

  const fortigateCode2 = `config firewall policy
    edit 0
        set name "Block-Threat-Live-IPs"
        set srcintf "any"
        set dstintf "any"
        set srcaddr "Threat-Live"
        set dstaddr "all"
        set service ALL
        set action deny
        set schedule always
        set status enable
        set logtraffic all
    next
end`;

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const CodeBlock = ({ code, index }) => (
    <div className="relative mt-3">
      <div className="absolute top-2 right-2 z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard(code, index)}
          className="h-8 px-3 sharp font-mono text-[10px] uppercase tracking-wider bg-[#0A0F14] border-emerald-900/30 hover:bg-[#0F1419] hover:border-primary text-muted-foreground hover:text-foreground"
        >
          {copiedIndex === index ? (
            <><Check className="h-3.5 w-3.5 mr-1.5 text-primary" /><span className="text-primary">{txt.copied}</span></>
          ) : (
            <><Copy className="h-3.5 w-3.5 mr-1.5" />{txt.copy}</>
          )}
        </Button>
      </div>
      <pre className="terminal bg-[#0A0F14] text-emerald-400/90 p-4 pr-24 sharp overflow-x-auto text-sm font-mono border border-emerald-900/20">
        <code>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="setup-page">
      <SEO page="setup" />
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="soc-badge mb-4">
            <span className="status-dot" />
            <span>// SETUP · R-04</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3 flex items-center gap-3">
            <Settings className="h-8 w-8 text-primary" />
            {txt.title}
          </h1>
          <p className="text-base text-muted-foreground max-w-3xl border-l-2 border-primary pl-4">{txt.description}</p>
        </div>

        {/* Device Selection Tabs */}
        <Tabs defaultValue="fortigate" className="max-w-5xl mx-auto">
          <div className="mb-6">
            <h3 className="text-sm font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">{txt.selectDevice}</h3>
            <TabsList className="flex flex-wrap justify-start gap-2 bg-transparent h-auto p-0">
              <TabsTrigger 
                value="fortigate" 
                className="flex items-center gap-2 px-4 py-2 sharp font-mono text-[10px] uppercase tracking-[0.14em] border border-border bg-card hover:bg-muted data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all"
              >
                <img src="/icons/fortinet.svg" alt="FortiGate" className="h-3.5 w-3.5 dark:invert data-[state=active]:invert" />
                FortiGate
              </TabsTrigger>
              <TabsTrigger 
                value="juniper" 
                className="flex items-center gap-2 px-4 py-2 sharp font-mono text-[10px] uppercase tracking-[0.14em] border border-border bg-card hover:bg-muted data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all"
              >
                <img src="/icons/router.svg" alt="Juniper" className="h-3.5 w-3.5 dark:invert data-[state=active]:invert" />
                Juniper
              </TabsTrigger>
              <TabsTrigger 
                value="mikrotik" 
                className="flex items-center gap-2 px-4 py-2 sharp font-mono text-[10px] uppercase tracking-[0.14em] border border-border bg-card hover:bg-muted data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all"
              >
                <img src="/icons/mikrotik.svg" alt="MikroTik" className="h-3.5 w-3.5 dark:invert data-[state=active]:invert" />
                MikroTik
              </TabsTrigger>
              <TabsTrigger 
                value="linux" 
                className="flex items-center gap-2 px-4 py-2 sharp font-mono text-[10px] uppercase tracking-[0.14em] border border-border bg-card hover:bg-muted data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all"
              >
                <img src="/icons/linux.svg" alt="Linux" className="h-3.5 w-3.5 dark:invert data-[state=active]:invert" />
                Linux
              </TabsTrigger>
              <TabsTrigger 
                value="windows" 
                className="flex items-center gap-2 px-4 py-2 sharp font-mono text-[10px] uppercase tracking-[0.14em] border border-border bg-card hover:bg-muted data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all"
              >
                <img src="/icons/windows.svg" alt="Windows" className="h-3.5 w-3.5 dark:invert data-[state=active]:invert" />
                Windows
              </TabsTrigger>
            </TabsList>
          </div>

          {/* FortiGate */}
          <TabsContent value="fortigate">
            <Card className="sharp border-border bg-card/50 backdrop-blur">
              <CardHeader className="border-b border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-mono">
                    <img src="/icons/fortinet.svg" alt="FortiGate" className="h-5 w-5 dark:invert" />
                    {txt.fortigateTitle}
                  </CardTitle>
                  <span className="soc-badge soc-badge--neutral">FIREWALL</span>
                </div>
                <CardDescription className="mt-2">{txt.fortigateDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Important Note */}
                <div className="flex items-start gap-3 p-4 sharp bg-amber-500/5 border border-amber-500/20">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-amber-500 mb-1">{txt.important}</h4>
                    <p className="text-sm text-muted-foreground">{txt.fortigateNote}</p>
                  </div>
                </div>

                {/* Step 1 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-7 h-7 sharp bg-primary/10 text-primary font-mono font-bold text-sm border border-primary/30">
                      1
                    </div>
                    <div>
                      <h4 className="font-mono font-semibold text-sm">{txt.fortigateStep1Title}</h4>
                      <p className="text-xs text-muted-foreground">{txt.fortigateStep1Desc}</p>
                    </div>
                  </div>
                  <div className="ml-10">
                    <Badge variant="outline" className="mb-2 sharp font-mono text-[9px] uppercase tracking-wider">FortiGate CLI</Badge>
                    <CodeBlock code={fortigateCode1} index={1} />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-7 h-7 sharp bg-primary/10 text-primary font-mono font-bold text-sm border border-primary/30">
                      2
                    </div>
                    <div>
                      <h4 className="font-mono font-semibold text-sm">{txt.fortigateStep2Title}</h4>
                      <p className="text-xs text-muted-foreground">{txt.fortigateStep2Desc}</p>
                    </div>
                  </div>
                  <div className="ml-10">
                    <Badge variant="outline" className="mb-2 sharp font-mono text-[9px] uppercase tracking-wider">FortiGate CLI</Badge>
                    <CodeBlock code={fortigateCode2} index={2} />
                    <div className="mt-3 p-3 sharp bg-orange-500/5 border border-orange-500/20">
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-orange-500 font-mono uppercase text-[10px] tracking-wider">Note:</strong> {txt.fortigateNatNote}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Complete */}
                <div className="flex items-start gap-3 p-4 sharp bg-primary/5 border border-primary/20">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-primary mb-1">{txt.setupComplete}</h4>
                    <p className="text-sm text-muted-foreground">{txt.fortigateComplete}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Juniper */}
          <TabsContent value="juniper">
            <Card className="sharp border-border bg-card/50 backdrop-blur">
              <CardHeader className="border-b border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-mono">
                    <img src="/icons/router.svg" alt="Juniper" className="h-5 w-5 dark:invert" />
                    {txt.juniperTitle}
                  </CardTitle>
                  <span className="soc-badge soc-badge--info">ROUTER</span>
                </div>
                <CardDescription className="mt-2">{txt.juniperDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="flex items-start gap-3 p-4 sharp bg-blue-500/5 border border-blue-500/20">
                  <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-blue-500 mb-1">{txt.important}</h4>
                    <p className="text-sm text-muted-foreground">{txt.juniperNote}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 sharp bg-muted/50 border border-border">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm">{txt.juniperInfo1}</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 sharp bg-muted/50 border border-border">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm">{txt.juniperInfo2}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MikroTik */}
          <TabsContent value="mikrotik">
            <Card className="sharp border-border bg-card/50 backdrop-blur">
              <CardHeader className="border-b border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-mono">
                    <img src="/icons/mikrotik.svg" alt="MikroTik" className="h-5 w-5 dark:invert" />
                    {txt.mikrotikTitle}
                  </CardTitle>
                  <span className="soc-badge soc-badge--error">ROUTER</span>
                </div>
                <CardDescription className="mt-2">{txt.mikrotikDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="flex items-start gap-3 p-4 sharp bg-red-500/5 border border-red-500/20">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-red-500 mb-1">{txt.important}</h4>
                    <p className="text-sm text-muted-foreground">{txt.mikrotikNote}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 sharp bg-muted/50 border border-border">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm">{txt.mikrotikInfo1}</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 sharp bg-muted/50 border border-border">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm">
                      {txt.mikrotikInfo2}{' '}
                      <a href="/ip-list" className="text-primary hover:underline inline-flex items-center gap-1 font-mono text-xs">
                        {txt.ipListLink}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Linux */}
          <TabsContent value="linux">
            <Card className="sharp border-border bg-card/50 backdrop-blur">
              <CardHeader className="border-b border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-mono">
                    <img src="/icons/linux.svg" alt="Linux" className="h-5 w-5 dark:invert" />
                    {txt.linuxTitle}
                  </CardTitle>
                  <span className="soc-badge soc-badge--warning">SERVER</span>
                </div>
                <CardDescription className="mt-2">{txt.linuxDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Note */}
                <div className="flex items-start gap-3 p-4 sharp bg-orange-500/5 border border-orange-500/20">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-orange-500 mb-1">{txt.important}</h4>
                    <p className="text-sm text-muted-foreground">{txt.linuxNote}</p>
                  </div>
                </div>

                {/* Install Command */}
                <div>
                  <h3 className="text-sm font-mono uppercase tracking-[0.18em] text-foreground mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="sharp bg-primary/10 text-primary border-primary/30 font-mono text-[9px]">
                      {txt.step} 1
                    </Badge>
                    {txt.linuxStep1Title}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">{txt.linuxStep1Desc}</p>
                  <CodeBlock code="curl -fsSL https://threat.live/server/install.sh | sudo bash" index="linux-install" />
                </div>

                {/* Features */}
                <div className="space-y-3">
                  <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{txt.linuxFeatures}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.linuxFeature1}</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.linuxFeature2}</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.linuxFeature3}</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.linuxFeature4}</span>
                    </div>
                  </div>
                </div>

                {/* Uninstall */}
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">{txt.linuxUninstall}</h4>
                  <CodeBlock code="curl -fsSL https://threat.live/server/uninstall.sh | sudo bash" index="linux-uninstall" />
                </div>

                {/* Useful Commands */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-sm font-mono uppercase tracking-[0.18em] text-foreground mb-2 flex items-center gap-2">
                    <Terminal className="h-4 w-4 text-orange-500" />
                    {txt.usefulCommands}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">{txt.usefulCommandsDesc}</p>
                  
                  <div className="space-y-3">
                    {/* Status Command */}
                    <div className="p-4 sharp bg-muted/30 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="sharp bg-blue-500/10 text-blue-500 border-blue-500/30 font-mono text-[9px] uppercase tracking-wider">
                          {txt.cmdStatus}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{txt.cmdStatusDesc}</p>
                      <CodeBlock code="threat-live status" index="cmd-status" />
                      <div className="mt-3">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">{txt.exampleOutput}</p>
                        <pre className="text-xs bg-[#0A0F14] text-emerald-400/80 p-3 sharp overflow-x-auto font-mono border border-emerald-900/20">
{`Threat.live Threat Feed
------------------------
Blocked IPs : 62700
Firewall    : ACTIVE
Next Update : Sun 19:59`}
                        </pre>
                      </div>
                    </div>

                    {/* Update Command */}
                    <div className="p-4 sharp bg-muted/30 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="sharp bg-primary/10 text-primary border-primary/30 font-mono text-[9px] uppercase tracking-wider">
                          {txt.cmdUpdate}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{txt.cmdUpdateDesc}</p>
                      <CodeBlock code="threat-live update" index="cmd-update" />
                    </div>

                    {/* Logs Command */}
                    <div className="p-4 sharp bg-muted/30 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="sharp bg-purple-500/10 text-purple-500 border-purple-500/30 font-mono text-[9px] uppercase tracking-wider">
                          {txt.cmdLogs}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{txt.cmdLogsDesc}</p>
                      <CodeBlock code="threat-live logs" index="cmd-logs" />
                    </div>
                  </div>
                </div>

                {/* Complete Message */}
                <div className="flex items-start gap-3 p-4 sharp bg-primary/5 border border-primary/20">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-primary mb-1">{txt.setupComplete}</h4>
                    <p className="text-sm text-muted-foreground">{txt.linuxComplete}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Windows */}
          <TabsContent value="windows">
            <Card className="sharp border-border bg-card/50 backdrop-blur">
              <CardHeader className="border-b border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-mono">
                    <img src="/icons/windows.svg" alt="Windows" className="h-5 w-5 dark:invert" />
                    {txt.windowsTitle}
                  </CardTitle>
                  <span className="soc-badge soc-badge--info">WORKSTATION</span>
                </div>
                <CardDescription className="mt-2">{txt.windowsDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Note */}
                <div className="flex items-start gap-3 p-4 sharp bg-blue-500/5 border border-blue-500/20">
                  <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-blue-500 mb-1">{txt.important}</h4>
                    <p className="text-sm text-muted-foreground">{txt.windowsNote}</p>
                  </div>
                </div>

                {/* Install Command */}
                <div>
                  <h3 className="text-sm font-mono uppercase tracking-[0.18em] text-foreground mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="sharp bg-primary/10 text-primary border-primary/30 font-mono text-[9px]">
                      {txt.step} 1
                    </Badge>
                    {txt.windowsStep1Title}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">{txt.windowsStep1Desc}</p>
                  <CodeBlock code='powershell -ExecutionPolicy Bypass -Command "iwr https://threat.live/server/install.ps1 | iex"' index="windows-install" />
                </div>

                {/* Features */}
                <div className="space-y-3">
                  <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{txt.windowsFeatures}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.windowsFeature1}</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.windowsFeature2}</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.windowsFeature3}</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 sharp bg-muted/50 border border-border">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs">{txt.windowsFeature4}</span>
                    </div>
                  </div>
                </div>

                {/* Useful Commands */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-sm font-mono uppercase tracking-[0.18em] text-foreground mb-2 flex items-center gap-2">
                    <Terminal className="h-4 w-4 text-blue-500" />
                    {txt.usefulCommands}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">{txt.usefulCommandsDesc}</p>
                  
                  <div className="space-y-3">
                    {/* Status Command */}
                    <div className="p-4 sharp bg-muted/30 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="sharp bg-blue-500/10 text-blue-500 border-blue-500/30 font-mono text-[9px] uppercase tracking-wider">
                          {txt.cmdStatus}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{txt.cmdStatusDesc}</p>
                      <CodeBlock code="threat-live status" index="win-cmd-status" />
                      <div className="mt-3">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">{txt.exampleOutput}</p>
                        <pre className="text-xs bg-[#0A0F14] text-emerald-400/80 p-3 sharp overflow-x-auto font-mono border border-emerald-900/20">
{`Threat.live Status
-------------------
Firewall rule : INSTALLED
Updater task  : INSTALLED
Blocked IPs   : 62700
Next update   : Scheduled every 10 minutes
Feed URL      : https://list.threat.live
Threat.live protection is ACTIVE`}
                        </pre>
                      </div>
                    </div>

                    {/* Update Command */}
                    <div className="p-4 sharp bg-muted/30 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="sharp bg-primary/10 text-primary border-primary/30 font-mono text-[9px] uppercase tracking-wider">
                          {txt.cmdUpdate}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{txt.cmdUpdateDesc}</p>
                      <CodeBlock code="threat-live update" index="win-cmd-update" />
                    </div>

                    {/* Uninstall Command */}
                    <div className="p-4 sharp bg-muted/30 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="sharp bg-red-500/10 text-red-500 border-red-500/30 font-mono text-[9px] uppercase tracking-wider">
                          {txt.cmdUninstall}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{txt.cmdUninstallDesc}</p>
                      <CodeBlock code="threat-live uninstall" index="win-cmd-uninstall" />
                    </div>
                  </div>
                </div>

                {/* Complete Message */}
                <div className="flex items-start gap-3 p-4 sharp bg-primary/5 border border-primary/20">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-xs uppercase tracking-wider text-primary mb-1">{txt.setupComplete}</h4>
                    <p className="text-sm text-muted-foreground">{txt.windowsComplete}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default SetupPage;
